import styled from '@emotion/styled';
import { Button, Icon, Select } from 'semantic-ui-react';
import moment from 'src/services/moment_instance';
import { screenSizes } from 'src/assets/styles/Literals';
import { dayInTZ, eventDisplayName, searchSelectProps } from 'src/components/helpers';

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const monthOptions = moment.months().map((name, index) => ({
  key: index,
  value: index,
  text: name.charAt(0).toUpperCase() + name.slice(1),
}));

export const buildMonthDays = (month) => {
  const start = month.clone().startOf('month').startOf('isoWeek');
  const end = month.clone().endOf('month').endOf('isoWeek');
  const days = [];
  const cursor = start.clone();

  while (cursor.isSameOrBefore(end, 'day')) {
    days.push(cursor.clone());
    cursor.add(1, 'day');
  }

  return days;
};

const Picker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 4px 0 16px;
`;

const CalendarWrap = styled.div`
  overflow-x: auto;

  .calendar {
    min-width: 760px;
    border: 1px solid #e1e7ed;
    border-radius: 10px;
    overflow: hidden;
  }

  .calendar__weekdays,
  .calendar__grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  .calendar__weekdays span {
    padding: 0.55rem 0.25rem;
    text-align: center;
    font-size: 0.8rem;
    font-weight: 700;
    color: #66737f;
    background: #f4f8fb;
    border-bottom: 1px solid #e1e7ed;
  }

  .day {
    min-height: 108px;
    padding: 0.35rem;
    background: #fff;
    border-top: 1px solid #e1e7ed;
    border-left: 1px solid #e1e7ed;
  }

  .day:nth-of-type(7n + 1) {
    border-left: 0;
  }

  .day--outside {
    background: #f7f8fa;
    color: #b0b8c0;
  }

  .day--today {
    box-shadow: inset 0 0 0 2px #2596c2;
  }

  .day--busy {
    background: #f3fafd;
  }

  .day__number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.6rem;
    height: 1.6rem;
    margin: 0;
    padding: 0 0.25rem;
    border-radius: 999px;
    font-weight: 700;
  }

  .day--today .day__number {
    background: #2596c2;
    color: #fff;
  }

  .service-chip {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    margin-top: 0.3rem;
    padding: 0.2rem 0.4rem;
    border: 0;
    border-radius: 4px;
    background: #2185d0;
    color: #fff;
    text-align: left;
    cursor: pointer;
  }

  .service-chip:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .service-chip__name {
    max-width: 100%;
    overflow: hidden;
    font-size: 0.68rem;
    font-weight: 600;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .service-chip__time {
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1.2;
  }

  .service-chip__meta {
    font-size: 0.68rem;
    font-weight: 400;
    opacity: 0.9;
  }

  @media (max-width: ${screenSizes.tablet}) {
    .day {
      min-height: 88px;
    }
  }
`;

export const ServiceMonthPicker = ({ month, onChange }) => {
  const year = month.year();
  const yearOptions = Array.from({ length: 11 }, (_, index) => {
    const value = year - 5 + index;
    return { key: value, value, text: String(value) };
  });

  const goTo = (nextMonth, nextYear = year) =>
    onChange(month.clone().year(nextYear).month(nextMonth).startOf('month'));

  return (
    <Picker>
      <Button
        type="button"
        basic
        icon
        aria-label="Mes anterior"
        onClick={() => onChange(month.clone().subtract(1, 'month').startOf('month'))}
      >
        <Icon name="chevron left" />
      </Button>
      <Select
        aria-label="Mes"
        compact
        {...searchSelectProps({
          value: month.month(),
          handler: (e, { value }) => goTo(value),
          options: monthOptions,
        })}
      />
      <Select
        aria-label="Año"
        compact
        {...searchSelectProps({
          value: year,
          handler: (e, { value }) => goTo(month.month(), value),
          options: yearOptions,
        })}
      />
      <Button
        type="button"
        basic
        icon
        aria-label="Mes siguiente"
        onClick={() => onChange(month.clone().add(1, 'month').startOf('month'))}
      >
        <Icon name="chevron right" />
      </Button>
    </Picker>
  );
};

const ServicesMonthCalendar = ({ month, services, loading, onOpenService }) => {
  const today = dayInTZ(new Date(), 'YYYY-MM-DD');
  const days = buildMonthDays(month);
  const hasServices = Object.keys(services).length > 0;

  return (
    <CalendarWrap>
      {!loading && !hasServices && <p>No hay servicios creados en este mes.</p>}
      {loading && <p>Cargando servicio...</p>}
      <div className="calendar">
        <div className="calendar__weekdays">
          {WEEKDAYS.map((weekday) => (
            <span key={weekday}>{weekday}</span>
          ))}
        </div>
        <div className="calendar__grid">
          {days.map((day) => {
            const key = day.format('YYYY-MM-DD');
            const inMonth = day.isSame(month, 'month');
            const dayServices = inMonth ? services[key] || [] : [];
            const className = [
              'day',
              inMonth ? '' : 'day--outside',
              key === today ? 'day--today' : '',
              dayServices.length ? 'day--busy' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div key={key} className={className}>
                <span className="day__number">{day.date()}</span>
                {dayServices.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    className="service-chip"
                    disabled={loading}
                    onClick={() => onOpenService(service)}
                  >
                    <span className="service-chip__name">
                      {eventDisplayName(service.eventSlugname)}
                    </span>
                    <span className="service-chip__time">{dayInTZ(service.date, 'hh:mm a')}</span>
                    <span className="service-chip__meta">
                      {service.AuditoriumGuests?.length || 0} asistentes
                    </span>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </CalendarWrap>
  );
};

export default ServicesMonthCalendar;
